import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  checking: boolean;
  isAllowed: boolean;
  userRole: 'admin' | 'user' | null;
  tenantId: string | null;
  tenantName: string | null;
  signOut: () => Promise<void>;
  loginLocalE2EUser?: (email: string) => void;
}

const E2E_SESSION_KEY = 'gwantt_e2e_local_user';

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  checking: false,
  isAllowed: false,
  userRole: null,
  tenantId: null,
  tenantName: null,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [userRole, setUserRole] = useState<'admin' | 'user' | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);

  const activateLocalE2EUser = (email: string) => {
    const normalizedEmail = email.toLowerCase().trim();
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(E2E_SESSION_KEY, normalizedEmail);
      sessionStorage.setItem('stratum_wizard_session_seen', 'true');
    }
    setUser({
      uid: 'e2e_local_uid',
      email: normalizedEmail,
      displayName: 'E2E Test Admin',
    } as User);
    setIsAllowed(true);
    setUserRole('admin');
    setTenantId('telfs');
    setTenantName('Gemeinde Telfs');
    setChecking(false);
    setLoading(false);
  };

  useEffect(() => {
    if (
      import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true' &&
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(E2E_SESSION_KEY) === 'test_e2e@telfs.gv.at'
    ) {
      activateLocalE2EUser('test_e2e@telfs.gv.at');
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (
        import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true' &&
        ((typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem(E2E_SESSION_KEY) === 'test_e2e@telfs.gv.at') ||
          currentUser?.email?.toLowerCase() === 'test_e2e@telfs.gv.at')
      ) {
        activateLocalE2EUser('test_e2e@telfs.gv.at');
        return;
      }
      setUser(currentUser);

      if (currentUser) {
        setChecking(true);
        try {
          // Primary lookup: by Firebase UID
          const userDoc = await getDoc(doc(db, 'allowedUsers', currentUser.uid));
          if (userDoc.exists() && userDoc.data().role) {
            setIsAllowed(true);
            setUserRole(userDoc.data().role as 'admin' | 'user');
            setTenantId(userDoc.data().tenantId || 'telfs');
            setTenantName(userDoc.data().tenantName || 'Gemeinde Telfs');
          } else if (currentUser.email) {
            // Fallback: lookup by lowercase email address
            const lowerEmail = currentUser.email.toLowerCase();
            const emailDoc = await getDoc(doc(db, 'allowedUsers', lowerEmail));
            if (emailDoc.exists()) {
              setIsAllowed(true);
              setUserRole(emailDoc.data().role as 'admin' | 'user' ?? 'user');
              setTenantId(emailDoc.data().tenantId || 'telfs');
              setTenantName(emailDoc.data().tenantName || 'Gemeinde Telfs');
            } else if (
              import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true' &&
              lowerEmail === 'test_e2e@telfs.gv.at'
            ) {
              // Auto-bootstrap E2E test account exclusively inside the local Firebase Emulator
              try {
                await setDoc(doc(db, 'allowedUsers', currentUser.uid), {
                  email: lowerEmail,
                  role: 'admin',
                  tenantId: 'telfs',
                  tenantName: 'Gemeinde Telfs',
                });
              } catch {
                // Ignore if emulator rules disallow client self-write to allowedUsers
              }
              setIsAllowed(true);
              setUserRole('admin');
              setTenantId('telfs');
              setTenantName('Gemeinde Telfs');
            } else {
              setIsAllowed(false);
              setUserRole(null);
              setTenantId(null);
              setTenantName(null);
            }
          } else {
            setIsAllowed(false);
            setUserRole(null);
            setTenantId(null);
            setTenantName(null);
          }
        } catch (error) {
          console.error('[Auth] Whitelist check failed:', error);
          setIsAllowed(false);
          setUserRole(null);
          setTenantId(null);
          setTenantName(null);
        } finally {
          setChecking(false);
        }
      } else {
        setIsAllowed(false);
        setUserRole(null);
        setTenantId(null);
        setTenantName(null);
        setChecking(false);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signOut = async () => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(E2E_SESSION_KEY);
    }
    setUser(null);
    setIsAllowed(false);
    setUserRole(null);
    setTenantId(null);
    setTenantName(null);
    try {
      await firebaseSignOut(auth);
    } catch {
      // Ignore signOut network errors in local emulator mode
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, checking, isAllowed, userRole, tenantId, tenantName, signOut, loginLocalE2EUser: activateLocalE2EUser }}>
      {children}
    </AuthContext.Provider>
  );
};
